import jsPDF from "jspdf";
import { svg2pdf } from "svg2pdf.js";
import SalesDealsApi from "api/admins/sales-deals-api";

// IMPORTANT: use bundler-resolved asset, same as OpsCreateInvoiceScreen
import InvoiceLogo from "assets/img/bnk-icon-black.png";

jsPDF.API.svg = function (svgElement, options) {
  return svg2pdf(svgElement, this, options);
};

const IMAGE_LOAD_TIMEOUT_MS = 12000;
const IMAGE_MAX_DIMENSION = 1600;
const PAID_STAMP_ASSET_URL = "/invoice-paid-stamp.png";

function errorMessage(error) {
  if (typeof error?.message === "string" && error.message.trim()) {
    return error.message.trim();
  }

  return String(error || "Unknown error");
}

function loadImage(url, { timeoutMs = IMAGE_LOAD_TIMEOUT_MS } = {}) {
  return new Promise((resolve, reject) => {
    if (!url) {
      resolve(null);
      return;
    }

    const img = new Image();
    let settled = false;
    let timeoutId;

    const cleanup = () => {
      img.onload = null;
      img.onerror = null;
      if (timeoutId) clearTimeout(timeoutId);
    };

    const finishResolve = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(img);
    };

    const finishReject = (error) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };

    // Must be assigned before src. The image host must allow the dashboard
    // origin through Access-Control-Allow-Origin.
    img.crossOrigin = "anonymous";
    img.decoding = "async";
    img.onload = finishResolve;
    img.onerror = () => {
      finishReject(new Error(`Failed to load invoice image: ${url}`));
    };

    timeoutId = setTimeout(() => {
      finishReject(new Error(`Timed out loading invoice image after ${timeoutMs}ms: ${url}`));
    }, timeoutMs);

    img.src = url;
  });
}

function imageToJpegDataUrl(img) {
  if (!img) return null;

  const sourceWidth = Number(img.naturalWidth || img.width || 0);
  const sourceHeight = Number(img.naturalHeight || img.height || 0);

  if (!sourceWidth || !sourceHeight) {
    throw new Error("Loaded invoice image has invalid dimensions");
  }

  const scale = Math.min(1, IMAGE_MAX_DIMENSION / Math.max(sourceWidth, sourceHeight));
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Canvas is unavailable while preparing invoice image");
  }

  // White background avoids transparent PNG becoming black in some PDF viewers.
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);
  context.drawImage(img, 0, 0, width, height);

  return canvas.toDataURL("image/jpeg", 0.88);
}

async function loadInvoiceImage(url, warnings) {
  if (!url) return null;

  try {
    const img = await loadImage(url);
    return imageToJpegDataUrl(img);
  } catch (error) {
    warnings.push({
      type: "image",
      url,
      message: errorMessage(error),
    });

    // One old/deleted/CORS-blocked image must never cancel the whole PDF.
    console.warn("[invoice-pdf] Image skipped", { url, error });
    return null;
  }
}

async function preloadInvoiceImages(invoiceItemList, warnings) {
  const urls = Array.from(
    new Set(
      (invoiceItemList || [])
        .flatMap((item) => {
          if (Array.isArray(item?.imageEntries) && item.imageEntries.length > 0) {
            return item.imageEntries.map((entry) => entry?.url);
          }

          return [item?.mainImageUrl, item?.stampImageUrl];
        })
        .filter(Boolean)
    )
  );

  // Broken legacy URLs now cost one timeout window, not N timeout windows.
  const entries = await Promise.all(
    urls.map(async (url) => [url, await loadInvoiceImage(url, warnings)])
  );

  return new Map(entries);
}

async function loadCheckmarkSvg(warnings) {
  try {
    const response = await fetch("/invoice-checkmark.svg", { cache: "force-cache" });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const svgText = await response.text();
    const parser = new DOMParser();
    const svgElement = parser.parseFromString(svgText, "image/svg+xml").documentElement;

    if (!svgElement || svgElement.nodeName.toLowerCase() !== "svg") {
      throw new Error("invoice-checkmark.svg did not return a valid SVG document");
    }

    return svgElement;
  } catch (error) {
    warnings.push({
      type: "asset",
      url: "/invoice-checkmark.svg",
      message: errorMessage(error),
    });
    console.warn("[invoice-pdf] Checkmark SVG unavailable; using bullet fallback", error);
    return null;
  }
}

async function drawDescriptionMarker(doc, svgElement, x, y, warnings) {
  if (svgElement) {
    try {
      await doc.svg(svgElement, {
        x,
        y,
        width: 2.5,
        height: 2.5,
      });
      return;
    } catch (error) {
      warnings.push({
        type: "asset",
        url: "/invoice-checkmark.svg",
        message: errorMessage(error),
      });
      console.warn("[invoice-pdf] SVG render failed; using bullet fallback", error);
    }
  }

  doc.circle(x + 1.1, y + 1.1, 0.45, "F");
}

function getCustomerDetail(deal) {
  // NEW: deal is tied to a selected user_detail
  return deal?.user_detail || deal?.user?.detail || null;
}

function formatPhoneFromDetail(detail) {
  if (!detail) return "";

  const rawPhone = (detail.phone || "").toString().trim();
  if (!rawPhone) return "";

  const codeRaw = detail.phone_code?.phone_code ?? detail.phone_code ?? detail.phone_code_id;
  let code = (codeRaw || "").toString().trim();

  // normalize to +62 style
  if (code) {
    code = code.replace(/^\+/, ""); // remove leading +
    code = `+${code}`; // re-add single +
  }

  if (!code) {
    return rawPhone.startsWith("+") ? rawPhone : rawPhone;
  }

  if (rawPhone.startsWith("+")) return rawPhone;

  const codeDigits = code.replace(/\D/g, "");
  if (codeDigits && rawPhone.startsWith(codeDigits)) {
    return `+${rawPhone}`;
  }

  return `${code}${rawPhone}`;
}

function formatAmountForDisplay(amount) {
  return new Intl.NumberFormat("id-ID").format(amount);
}

function parseMoneyAmount(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  if (value === null || value === undefined) {
    return 0;
  }

  const raw = value.toString().trim();
  if (!raw) {
    return 0;
  }

  const cleaned = raw.replace(/IDR|Rp/gi, "").replace(/\s/g, "");
  const normalized = (() => {
    if (cleaned.includes(",")) {
      return cleaned.replace(/\./g, "").replace(",", ".");
    }

    const dotParts = cleaned.split(".");
    if (dotParts.length > 1 && dotParts.slice(1).every((part) => part.length === 3)) {
      return dotParts.join("");
    }

    return cleaned.replace(/,/g, "");
  })();
  const parsed = Number(normalized);

  return Number.isFinite(parsed) ? parsed : 0;
}

function getVoucherCode(deal = {}) {
  const details = deal?.voucher_details || {};

  return (
    deal?.voucher_code ||
    details?.code ||
    details?.voucher_code ||
    deal?.voucher?.code ||
    ""
  )
    .toString()
    .trim();
}

function getVoucherDiscountAmount(deal = {}) {
  return Math.max(parseMoneyAmount(deal?.discount_amount), 0);
}

function getShipmentCostAmount(deal = {}) {
  const totalShippingFee = deal?.total_shipping_fee;
  if (totalShippingFee !== null && totalShippingFee !== undefined && totalShippingFee !== "") {
    return Math.max(parseMoneyAmount(totalShippingFee), 0);
  }

  const shipmentTotalFee = deal?.shipment?.total_fee;
  if (shipmentTotalFee !== null && shipmentTotalFee !== undefined && shipmentTotalFee !== "") {
    return Math.max(parseMoneyAmount(shipmentTotalFee), 0);
  }

  const dealShippingBreakdown =
    parseMoneyAmount(deal?.discounted_delivery_fee) +
    parseMoneyAmount(deal?.insurance_fee);
  if (dealShippingBreakdown > 0) return dealShippingBreakdown;

  return Math.max(
    parseMoneyAmount(deal?.shipment?.delivery_fee) +
      parseMoneyAmount(deal?.shipment?.insurance_fee),
    0
  );
}

const CONFIRMED_PAYMENT_STATES = ["dp_confirmed", "capture", "settlement"];
const CUSTOMER_RECEIPT_PAYMENT_STATES = ["dp_confirmed", "settlement"];

function getPaymentsAsc(deal = {}) {
  const list = Array.isArray(deal?.payments)
    ? deal.payments.filter(Boolean)
    : (deal?.payment ? [deal.payment] : []);

  return list.slice().sort((a, b) => {
    const ta = new Date(a?.created_at || a?.updated_at || 0).getTime();
    const tb = new Date(b?.created_at || b?.updated_at || 0).getTime();
    return ta - tb;
  });
}

function getConfirmedReceiptPayments(deal = {}) {
  return getPaymentsAsc(deal).filter((payment) =>
    CUSTOMER_RECEIPT_PAYMENT_STATES.includes(payment?.state)
  );
}

function getConfirmedPaymentTotal(deal = {}) {
  if (deal?.confirmed_payment_total !== null && deal?.confirmed_payment_total !== undefined) {
    return Math.max(parseMoneyAmount(deal.confirmed_payment_total), 0);
  }

  return getPaymentsAsc(deal)
    .filter((payment) => CONFIRMED_PAYMENT_STATES.includes(payment?.state))
    .reduce((sum, payment) => sum + parseMoneyAmount(payment?.amount), 0);
}

function getOutstandingBalance(deal = {}, netAmount = 0) {
  if (deal?.payment_outstanding_balance !== null && deal?.payment_outstanding_balance !== undefined) {
    return Math.max(parseMoneyAmount(deal.payment_outstanding_balance), 0);
  }

  return Math.max(netAmount - getConfirmedPaymentTotal(deal), 0);
}

function getLatestCustomerReceiptDate(deal = {}) {
  const confirmedPayments = getConfirmedReceiptPayments(deal);

  for (let index = confirmedPayments.length - 1; index >= 0; index -= 1) {
    const payment = confirmedPayments[index];
    const value = payment?.paid_at || payment?.finance_confirmed_at;
    if (value) return value;
  }

  return null;
}

function formatDocumentDate(value, timeZone = null) {
  const date = value ? new Date(value) : new Date();
  const safeDate = Number.isNaN(date.getTime()) ? new Date() : date;

  return safeDate.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    ...(timeZone ? { timeZone } : {}),
  });
}

function formatPaymentSummaryDate(value) {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function getQty(item = {}) {
  return parseInt(item?.quantity, 10) || 1;
}

function getUnitAmount(item = {}) {
  return parseMoneyAmount(item?.unitAmount);
}

function getLineAmount(item = {}) {
  const amount = parseMoneyAmount(item?.amount);
  if (amount > 0) return amount;
  return getUnitAmount(item) * getQty(item);
}

function getProductGroupCode(item = {}) {
  const product = item?.product || {};

  return (
    item?.group_code ||
    product?.group_code ||
    product?.group?.code ||
    product?.summary_index?.group_code ||
    product?.summary_index?.group?.code ||
    product?.summary_index?.group_code_name ||
    ""
  )
    .toString()
    .trim()
    .toUpperCase();
}

function isBktSku(item = {}) {
  const groupCode = getProductGroupCode(item);
  if (groupCode === "BKT") return true;

  const skuCandidates = [
    item?.sku,
    item?.product?.sku,
    item?.product?.product_sku,
    item?.product?.sku_code,
    item?.product?.summary_index?.sku,
  ];

  return skuCandidates.some((sku) => /^BKT/i.test((sku || "").toString().trim()));
}

function escapeRegExp(value = "") {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function getProductBrand(product = {}) {
  const summaryBrand = product?.summary_index?.brand;

  if (typeof summaryBrand === "string" && summaryBrand.trim()) {
    return summaryBrand.trim();
  }

  if (summaryBrand && typeof summaryBrand === "object") {
    const summaryBrandName = (summaryBrand?.name || summaryBrand?.title || "").toString().trim();
    if (summaryBrandName) return summaryBrandName;
  }

  return (
    product?.brand?.name ||
    product?.brand_name ||
    product?.summary_index?.brand_name ||
    ""
  )
    .toString()
    .trim();
}

function stripLeadingBrand(value = "", brand = "") {
  const text = (value || "").toString().trim();
  const brandName = (brand || "").toString().trim();

  if (!text || !brandName) return text;

  return text
    .replace(
      new RegExp(`^${escapeRegExp(brandName)}(?:\\s+|\\s*-\\s*|\\s*:\\s*)?`, "i"),
      ""
    )
    .trim();
}

function getInvoiceTitle(item = {}) {
  const product = item?.product || {};
  const bkt = isBktSku(item);
  const brand = getProductBrand(product);

  const condition = (product?.summary_index?.condition || "").toString().toUpperCase();

  const displayName = bkt
    ? stripLeadingBrand(product?.name, brand)
    : (product?.name || "").toString().trim();

  const displayStamp = bkt
    ? stripLeadingBrand(product?.stamp, brand)
    : (product?.stamp || "").toString().trim();

  return [
    !bkt ? condition : "",
    !bkt ? brand : "",
    displayName,
    displayStamp,
  ]
    .filter(Boolean)
    .join(" ");
}

function getInvoiceSkuSuffix(item = {}) {
  const product = item?.product || {};
  const sku = (
    item?.sku ||
    product?.sku ||
    product?.product_sku ||
    product?.sku_code ||
    product?.summary_index?.sku ||
    ""
  )
    .toString()
    .trim();

  return sku ? sku.slice(-5) : "";
}

export async function buildInvoiceDoc(
  deal,
  {
    displayHeaderAndFooter = true,
    includeDownPayment = true,
    includeVoucher = true,
    documentTitle = "INVOICE",
    summaryMode = "invoice",
    documentDate = null,
    showPaidStamp = false,
  } = {}
) {
  const displayCustomerName = true;
  const displayCustomerAddress = true;
  const displayCustomerPhone = true;
  const displayPrices = true;

  const customerDetail = getCustomerDetail(deal);
  const customerName = (customerDetail?.name || deal?.user?.email || "").toString();
  const isSelfPickup = deal?.shipment?.method_type === "self_pick_up";
  const customerAddress = (
    isSelfPickup
      ? ""
      : (deal?.shipment?.destination?.address || deal?.address?.complete_address || "")
  ).toString();

  // deal?.shipment?.recipient?.phone_number ||
  const customerPhone = formatPhoneFromDetail(customerDetail) || "";

  const referenceNumber = (deal?.reference_number ?? "").toString();
  const formattedInvoiceDate = formatDocumentDate(documentDate);
  const clientAdvisor = (deal?.sales_name_in_invoice || "N/A").toString();
  const shippedVia = (deal?.shipment?.courier_detail?.logistic?.name || "N/A").toString();
  const downPayment = includeDownPayment ? parseMoneyAmount(deal?.down_payment) : 0;
  const voucherDiscountAmount = includeVoucher ? getVoucherDiscountAmount(deal) : 0;
  const voucherCode = includeVoucher ? getVoucherCode(deal) : "";
  const footerAddress = "id";

  const invoiceItemList = (deal?.cart?.cart_items || []).map((item) => {
    const qty = parseInt(item?.quantity, 10) || 1;
    const unitAmount = parseMoneyAmount(item?.price);

    return {
      mainImageUrl: item?.product?.collect_invoice_photos?.comes_with_photo,
      stampImageUrl: item?.product?.collect_invoice_photos?.stamp_photo,
      cardImageUrl: item?.product?.collect_invoice_photos?.card_photo,
      imageEntries: [
        {
          label: "KELENGKAPAN",
          url: item?.product?.collect_invoice_photos?.comes_with_photo,
        },
        {
          label: "STAMP",
          url: item?.product?.collect_invoice_photos?.stamp_photo,
        },
        {
          label: "CARD",
          url: item?.product?.collect_invoice_photos?.card_photo,
        },
      ].filter((entry) => entry?.url),
      title: getInvoiceTitle(item),
      description: item?.product?.comes_with || "",
      quantity: qty,
      currency: "IDR",
      unitAmount,
      amount: unitAmount * qty,
    };
  });

  const invoiceWarnings = [];
  const imageMap = await preloadInvoiceImages(invoiceItemList, invoiceWarnings);
  const paidStampImageData =
    showPaidStamp
      ? await loadInvoiceImage(PAID_STAMP_ASSET_URL, invoiceWarnings)
      : null;

  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a5",
  });

  const svgElement = await loadCheckmarkSvg(invoiceWarnings);

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 18;

  // Top start position (reduce empty gap before "INVOICE")
  let yPos = displayHeaderAndFooter ? 55 : 44;

  // HEADER LOGO — EXACTLY like OpsCreateInvoiceScreen (conditional)
  if (displayHeaderAndFooter) {
    try {
      const logoImg = await loadImage(InvoiceLogo);
      const logoWidth = 50; // matches ~188px preview width
      const logoHeight = 13;

      doc.addImage(
        logoImg,
        "PNG",
        (pageWidth - logoWidth) / 2,
        yPos - logoHeight - 2,
        logoWidth,
        logoHeight
      );

      yPos += 8;
    } catch (error) {
      invoiceWarnings.push({
        type: "asset",
        url: InvoiceLogo,
        message: errorMessage(error),
      });
      console.warn("[invoice-pdf] Header logo skipped", error);
    }
  }

  // Document title
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text(documentTitle, margin, yPos);
  yPos += 8;

  // Two-column layout
  const leftColX = margin;
  const rightColX = pageWidth / 2 + 15;
  const startYPos = yPos;

  doc.setFontSize(6.5);

  let leftYPos = startYPos;
  const headerLeftXOffset = 23;

  if (displayCustomerName) {
    doc.setFont("helvetica", "bold");
    doc.text("SOLD TO:", leftColX, leftYPos);
    doc.text(customerName || "-", leftColX + headerLeftXOffset, leftYPos);
    leftYPos += 5;
  }

  if (displayCustomerAddress) {
    doc.setFont("helvetica", "bold");
    doc.text("ADDRESS:", leftColX, leftYPos);
    const addressLines = doc.splitTextToSize(customerAddress, 40);
    doc.text(addressLines, leftColX + headerLeftXOffset, leftYPos);
    leftYPos += addressLines.length * 4;
  }

  if (displayCustomerPhone) {
    doc.setFont("helvetica", "bold");
    doc.text("PHONE NUMBER:", leftColX, leftYPos);
    doc.text(customerPhone || "-", leftColX + headerLeftXOffset, leftYPos);
    leftYPos += 5;
  }

  let rightYPos = startYPos;
  const headerRightXOffset = 23;

  doc.setFont("helvetica", "bold");
  doc.text("NO:", rightColX, rightYPos);
  doc.text(referenceNumber, rightColX + headerRightXOffset, rightYPos);
  rightYPos += 5;

  doc.text("DATE:", rightColX, rightYPos);
  doc.text(formattedInvoiceDate, rightColX + headerRightXOffset, rightYPos);
  rightYPos += 5;

  doc.text("CLIENT ADVISOR:", rightColX, rightYPos);
  doc.text(clientAdvisor, rightColX + headerRightXOffset, rightYPos);
  rightYPos += 5;

  doc.text("SHIPPED VIA:", rightColX, rightYPos);
  doc.text(shippedVia, rightColX + headerRightXOffset, rightYPos);

  yPos = Math.max(leftYPos, rightYPos) + 4;

  // TABLE
  doc.setFontSize(4.8);
  const descColX = leftColX;
  const qtyColX = 87;
  const unitColX = 112;
  const amountColX = 132;

  doc.setFont("helvetica", "bold");
  doc.text("DESCRIPTION", descColX, yPos);
  if (displayPrices) {
    doc.text("QTY", qtyColX, yPos, { align: "center" });
    doc.text("UNIT PRICE", unitColX, yPos, { align: "right" });
    doc.text("SUB TOTAL", amountColX, yPos, { align: "right" });
  }
  yPos += 1.5;

  const lineExtraLength = 4;
  doc.line(leftColX - lineExtraLength, yPos, pageWidth - margin + lineExtraLength, yPos);
  yPos += 4;

  doc.setFont("helvetica", "normal");

  for (const item of invoiceItemList) {
    const itemStartY = yPos;
    let imageSectionHeight = 0;
    const mainImageHeight = 20;

    if (item.mainImageUrl) {
      const imageData = imageMap.get(item.mainImageUrl);
      if (imageData) {
        try {
          doc.addImage(
            imageData,
            "JPEG",
            descColX,
            itemStartY,
            (mainImageHeight * 3) / 4,
            mainImageHeight
          );
          imageSectionHeight += mainImageHeight;
        } catch (error) {
          invoiceWarnings.push({
            type: "image-render",
            url: item.mainImageUrl,
            message: errorMessage(error),
          });
          console.warn("[invoice-pdf] Main image render skipped", error);
        }
      }
    }

    if (item.stampImageUrl) {
      const imageData = imageMap.get(item.stampImageUrl);
      if (imageData) {
        try {
          doc.addImage(
            imageData,
            "JPEG",
            descColX,
            itemStartY + mainImageHeight + 2,
            (mainImageHeight * 3) / 4,
            mainImageHeight
          );
          imageSectionHeight += mainImageHeight + 2;
        } catch (error) {
          invoiceWarnings.push({
            type: "image-render",
            url: item.stampImageUrl,
            message: errorMessage(error),
          });
          console.warn("[invoice-pdf] Stamp image render skipped", error);
        }
      }
    }

    const itemTextOffset = 20;
    doc.setFont("helvetica", "bold");
    const titleLines = doc.splitTextToSize(item.title, 40);
    doc.text(titleLines, descColX + itemTextOffset, yPos);
    yPos += titleLines.length * 3;

    doc.setFont("helvetica", "normal");
    const descLines = item.description.split(", ").filter(Boolean);
    for (const line of descLines) {
      await drawDescriptionMarker(
        doc,
        svgElement,
        descColX + itemTextOffset,
        yPos - 2,
        invoiceWarnings
      );
      doc.text(line, descColX + itemTextOffset + 3.5, yPos);
      yPos += 3;
    }

    if (displayPrices) {
      doc.text(String(getQty(item)), qtyColX, itemStartY, { align: "center" });
      doc.text(`IDR ${formatAmountForDisplay(getUnitAmount(item))}`, unitColX, itemStartY, {
        align: "right",
      });
      doc.text(`IDR ${formatAmountForDisplay(getLineAmount(item))}`, amountColX, itemStartY, {
        align: "right",
      });
    }

    yPos = Math.max(yPos, itemStartY + imageSectionHeight) + 2;
  }

  const subtotal = invoiceItemList.reduce((sum, item) => sum + getLineAmount(item), 0);
  // final_payment is the canonical payable amount in Ops (already reflects voucher /
  // shipping / payment fee rules). Use it as NET so receipt/invoice totals stay
  // consistent with Finance settlement and outstanding calculations.
  const canonicalNetAmount = parseMoneyAmount(deal?.final_payment);
  const netAmount = canonicalNetAmount > 0
    ? canonicalNetAmount
    : Math.max(subtotal - voucherDiscountAmount, 0);
  const confirmedReceiptPayments = getConfirmedReceiptPayments(deal);
  const outstandingBalance = getOutstandingBalance(deal, netAmount);

  const drawSummaryRow = (label, amount, { negative = false } = {}) => {
    doc.setFont("helvetica", "bold");
    doc.text(label, unitColX, yPos, { align: "right" });
    const prefix = negative ? "- IDR " : "IDR ";
    doc.text(`${prefix}${formatAmountForDisplay(amount)}`, amountColX, yPos, { align: "right" });
    yPos += 5;
  };

  if (displayPrices) {
    if (summaryMode === "raw") {
      drawSummaryRow("Total", subtotal);
    } else if (summaryMode === "legacy") {
      if (voucherDiscountAmount > 0) {
        drawSummaryRow("Subtotal", subtotal);
        const voucherLabel = voucherCode ? `Voucher Discount (${voucherCode})` : "Voucher Discount";
        drawSummaryRow(voucherLabel, voucherDiscountAmount, { negative: true });
      }

      if (downPayment > 0) {
        drawSummaryRow("DP", downPayment);
      }

      drawSummaryRow("Total", Math.max(subtotal - voucherDiscountAmount - downPayment, 0));
    } else if (summaryMode === "invoice") {
      drawSummaryRow("Sub Total", subtotal);

      if (voucherDiscountAmount > 0) {
        drawSummaryRow("Voucher", voucherDiscountAmount, { negative: true });
      }

      drawSummaryRow("Total", netAmount);
    } else if (summaryMode === "customer_receipt") {
      drawSummaryRow("Sub Total", subtotal);

      if (voucherDiscountAmount > 0) {
        drawSummaryRow("Voucher", voucherDiscountAmount, { negative: true });
      }

      confirmedReceiptPayments.forEach((payment, index) => {
        const confirmedAt = payment?.paid_at || payment?.finance_confirmed_at;
        const dateLabel = confirmedAt ? ` - ${formatDocumentDate(confirmedAt)}` : "";
        drawSummaryRow(`DP ${index + 1}${dateLabel}`, parseMoneyAmount(payment?.amount));
      });

      drawSummaryRow("Outstanding Balance", outstandingBalance);
    }
  }

if (showPaidStamp && outstandingBalance <= 0 && ["invoice", "customer_receipt"].includes(summaryMode)) {
  const drawFallbackPaidStamp = () => {
    const fallbackStampX = amountColX - 2;
    const fallbackStampY = yPos + 2;

    doc.setDrawColor(180, 0, 0);
    doc.setTextColor(180, 0, 0);
    doc.setLineWidth(0.6);
    doc.rect(fallbackStampX - 23, fallbackStampY - 5, 23, 9);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("PAID", fallbackStampX - 11.5, fallbackStampY + 1.5, { align: "center" });
    doc.setTextColor(0, 0, 0);
    doc.setDrawColor(0, 0, 0);
  };

  if (paidStampImageData) {
    try {
      const stampWidth = 30;
      const stampHeight = 18.3;
      const stampX = amountColX - stampWidth + 2;
      const stampY = yPos + 1;

      doc.addImage(paidStampImageData, "PNG", stampX, stampY, stampWidth, stampHeight);
    } catch (error) {
      invoiceWarnings.push({
        type: "asset",
        url: PAID_STAMP_ASSET_URL,
        message: errorMessage(error),
      });
      console.warn("[invoice-pdf] Paid stamp render failed; using text fallback", error);
      drawFallbackPaidStamp();
    }
  } else {
    drawFallbackPaidStamp();
  }

  doc.setFontSize(4.8);
  yPos += 23;
}

  doc.line(leftColX - lineExtraLength, yPos, pageWidth - margin + lineExtraLength, yPos);
  yPos += 3;

  if (summaryMode === "customer_receipt") {
    doc.setFontSize(5.2);
    doc.setFont("helvetica", "normal");
    const customerReceiptTerms =
      "*Pembayaran telah kami terima. Tanda terima ini merupakan bukti bahwa pembayaran atas transaksi yang tercantum di atas telah diterima. Harap simpan tanda terima ini sebagai bukti pembayaran. Seluruh pembayaran yang telah dilakukan bersifat final dan tidak dapat dikembalikan (non-refundable). Tanda terima ini bukan merupakan faktur pajak.";
    const customerReceiptTermLines = doc.splitTextToSize(
      customerReceiptTerms,
      pageWidth - margin * 2
    );
    doc.text(customerReceiptTermLines, leftColX, yPos);
    yPos += customerReceiptTermLines.length * 2.4 + 3;
    doc.setFontSize(4.8);
  }

  if (summaryMode !== "customer_receipt") {
    doc.setFont("helvetica", "normal");
    const disclaimerLines = doc.splitTextToSize(
      "*Semua barang promo (harga khusus atau special price) yang sudah dibeli di BELLE&KATE Room adalah final. Kami tidak menerima klaim pengembalian dana atau pertukaran barang setelah barang dipesan. Terima kasih!",
      pageWidth - margin * 2
    );
    doc.text(disclaimerLines, leftColX, yPos);
    yPos += disclaimerLines.length * 2;
  }

  // FOOTER
  if (displayHeaderAndFooter) {
    yPos = pageHeight - 20;
    doc.setFontSize(5);
    doc.setFont("helvetica", "bold");

    if (footerAddress === "sg") {
      doc.text("313@Somerset", pageWidth / 2, yPos, { align: "center" });
      doc.text("313 Orchard Rd, Level B1 Number 16, Singapore 238895", pageWidth / 2, yPos + 3, {
        align: "center",
      });
    } else {
      doc.text("JL. JEND. SUDIRMAN KAV 52-53. LOT 22, RT 5 / RW 3, SENAYAN.", pageWidth / 2, yPos, {
        align: "center",
      });
      doc.text(
        "KBY. BARU, KOTA JAKARTA SELATAN, DAERAH KHUSUS IBUKOTA JAKARTA 12190",
        pageWidth / 2,
        yPos + 3,
        { align: "center" }
      );
    }
  }

  // Non-standard metadata used by the calling UI. The PDF remains valid.
  doc.invoiceWarnings = invoiceWarnings;

  return doc;
}


function buildDocumentDownloadFilename(prefix, referenceNumber) {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, "0");
  const timestamp = [
    now.getFullYear(),
    pad(now.getMonth() + 1),
    pad(now.getDate()),
    "-",
    pad(now.getHours()),
    pad(now.getMinutes()),
    pad(now.getSeconds()),
  ].join("");
  const safeReference = (referenceNumber || "deal")
    .toString()
    .trim()
    .replace(/[^a-zA-Z0-9_-]+/g, "-");

  return `${prefix}-${timestamp}-${safeReference}.pdf`;
}

async function buildPaginatedPaymentDocument(
  deal,
  {
    documentTitle,
    summaryMode,
    documentDate = null,
    displayBranding = true,
  }
) {
  const isCustomerReceipt = summaryMode === "customer_receipt";
  // ONE_ITEM_ONE_PAGE_PAID_STAMP_V1
  // Hard business rule: one purchased product is rendered on one A5 page.
  // Header/footer branding stays enabled by default for Print/Email flows.
  // When branding is hidden from the Sales Deal UI, keep the same reserved
  // A5 header/footer layout so toggling branding never changes pagination.
  const itemsPerPage = 1;
  const reserveHeaderFooterLayout = true;
  const customerDetail = getCustomerDetail(deal);
  const oldInvoice = deal?.legacy_inventory_invoice_data;
  const customerName = (oldInvoice?.customer_name || customerDetail?.name || deal?.user?.email || "").toString();
  const isSelfPickup = deal?.shipment?.method_type === "self_pick_up";
  const customerAddress = (
    oldInvoice?.customer_address || (isSelfPickup
      ? ""
      : (deal?.shipment?.destination?.address || deal?.address?.complete_address || ""))
  ).toString();
  const customerPhone = oldInvoice?.customer_phone || formatPhoneFromDetail(customerDetail) || "";
  const referenceNumber = (deal?.reference_number ?? "").toString();
  const formattedDocumentDate = formatDocumentDate(documentDate, oldInvoice?.inventory_id ? "Asia/Jakarta" : null);
  const clientAdvisor = (deal?.sales_name_in_invoice || "N/A").toString();
  const shippedVia = (
    isSelfPickup
      ? "Self Pick Up"
      : (deal?.shipment?.courier_detail?.logistic?.name || "N/A")
  ).toString();
  const voucherDiscountAmount = getVoucherDiscountAmount(deal);
  const shipmentCostAmount = getShipmentCostAmount(deal);

  // Customer Receipt is intentionally text-only for product evidence: keep the
  // product/Kelengkapan information, but never render Kelengkapan/Stamp/Card photos.
  // Invoice continues using all existing product photos unchanged.
  const receiptSuppressesProductPhotos = isCustomerReceipt;
  const invoiceItemList = (deal?.cart?.cart_items || []).map((item) => {
    const qty = parseInt(item?.quantity, 10) || 1;
    const unitAmount = parseMoneyAmount(item?.price);

    return {
      mainImageUrl: receiptSuppressesProductPhotos
        ? null
        : item?.product?.collect_invoice_photos?.comes_with_photo,
      stampImageUrl: receiptSuppressesProductPhotos
        ? null
        : item?.product?.collect_invoice_photos?.stamp_photo,
      cardImageUrl: receiptSuppressesProductPhotos
        ? null
        : item?.product?.collect_invoice_photos?.card_photo,
      imageEntries: receiptSuppressesProductPhotos
        ? []
        : [
            {
              label: "KELENGKAPAN",
              url: item?.product?.collect_invoice_photos?.comes_with_photo,
            },
            {
              label: "STAMP",
              url: item?.product?.collect_invoice_photos?.stamp_photo,
            },
            {
              label: "CARD",
              url: item?.product?.collect_invoice_photos?.card_photo,
            },
          ].filter((entry) => entry?.url),
      title: getInvoiceTitle(item),
      skuSuffix: getInvoiceSkuSuffix(item),
      description: item?.product?.comes_with || "",
      quantity: qty,
      currency: "IDR",
      unitAmount,
      amount: unitAmount * qty,
    };
  });

  const totalQty = invoiceItemList.reduce((sum, item) => sum + getQty(item), 0);
  const subtotal = invoiceItemList.reduce((sum, item) => sum + getLineAmount(item), 0);
  const canonicalNetAmount = parseMoneyAmount(deal?.final_payment);
  const netAmount = canonicalNetAmount > 0
    ? canonicalNetAmount
    : Math.max(subtotal - voucherDiscountAmount + shipmentCostAmount, 0);
  const confirmedReceiptPayments = getConfirmedReceiptPayments(deal);
  // Head Sales requirement: a Customer Receipt for one product must stay on
  // one A5 page for the normal payment history (up to five confirmed DPs).
  // More than five DPs keeps the existing safe pagination instead of clipping.
  const forceSinglePageCustomerReceipt =
    isCustomerReceipt &&
    invoiceItemList.length === 1 &&
    confirmedReceiptPayments.length <= 5;
  const totalPaid = confirmedReceiptPayments.reduce(
    (sum, payment) => sum + parseMoneyAmount(payment?.amount),
    0
  );
  const outstandingBalance = getOutstandingBalance(deal, netAmount);

  const invoiceWarnings = [];
  const imageMap = await preloadInvoiceImages(invoiceItemList, invoiceWarnings);
  const paidStampImageData = outstandingBalance <= 0
    ? await loadInvoiceImage(PAID_STAMP_ASSET_URL, invoiceWarnings)
    : null;
  const svgElement = await loadCheckmarkSvg(invoiceWarnings);

  let logoImg = null;
  if (displayBranding) {
    try {
      logoImg = await loadImage(InvoiceLogo);
    } catch (error) {
      invoiceWarnings.push({
        type: "asset",
        url: InvoiceLogo,
        message: errorMessage(error),
      });
    }
  }

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a5" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 16;
  const leftColX = margin;
  const rightColX = 84;
  const descColX = leftColX;
  const qtyColX = 85;
  const unitColX = 110;
  const amountColX = 131;
  const footerY = pageHeight - 20;
  // Product pages may use more vertical space than the final summary page.
  // Keep the summary limit conservative for terms/stamp, but allow up to
  // two product blocks to stay together on one page as required by design.
  const productContentBottomY = reserveHeaderFooterLayout ? footerY - 6 : pageHeight - 18;
  const contentBottomY = forceSinglePageCustomerReceipt ? 169 : 165;
  const termsLineY = forceSinglePageCustomerReceipt ? 168.5 : 164.5;

  // svg2pdf mutates jsPDF drawing state (stroke color / line width).
  // Always normalize it before drawing page chrome or continuing item text,
  // otherwise the next page can inherit the red checkmark stroke as a
  // very thick horizontal line.
  const resetDrawingState = () => {
    doc.setDrawColor(0, 0, 0);
    doc.setFillColor(0, 0, 0);
    doc.setTextColor(0, 0, 0);
    doc.setLineWidth(0.2);
  };

  const drawFooter = () => {
    resetDrawingState();
    if (!displayBranding) return;

    doc.setFontSize(5);
    doc.setFont("helvetica", "bold");
    doc.text(
      "JL. JEND. SUDIRMAN KAV 52-53. LOT 22, RT 5 / RW 3, SENAYAN.",
      pageWidth / 2,
      footerY,
      { align: "center" }
    );
    doc.text(
      "KBY. BARU, KOTA JAKARTA SELATAN, DAERAH KHUSUS IBUKOTA JAKARTA 12190",
      pageWidth / 2,
      footerY + 3,
      { align: "center" }
    );
  };

  const drawPageHeader = ({ showProductColumns = true } = {}) => {
    resetDrawingState();

    let yPos = reserveHeaderFooterLayout ? 34 : 44;

    if (displayBranding && logoImg) {
      const logoWidth = 50;
      const logoHeight = 13;
      doc.addImage(
        logoImg,
        "PNG",
        (pageWidth - logoWidth) / 2,
        yPos - logoHeight - 2,
        logoWidth,
        logoHeight
      );
      yPos += 8;
    }

    doc.setFontSize(15);
    doc.setFont("helvetica", "bold");
    doc.text(documentTitle, margin, yPos);

    const startYPos = yPos + 9.5;
    const headerLeftXOffset = 23;
    const headerRightXOffset = 26;
    const headerRowHeight = 4.3;
    const headerValueFontSize = 7;
    const headerValueLineHeight = 3.8;

    doc.setFontSize(5.6);
    doc.setFont("helvetica", "bold");

    let leftYPos = startYPos;
    doc.text("SOLD TO", leftColX, leftYPos);
    doc.text(":", leftColX + headerLeftXOffset - 2, leftYPos);
    doc.setFontSize(headerValueFontSize);
    const customerNameLines = doc.splitTextToSize(customerName || "-", 42);
    doc.text(customerNameLines, leftColX + headerLeftXOffset, leftYPos);
    doc.setFontSize(5.6);
    leftYPos += Math.max(customerNameLines.length, 1) * headerValueLineHeight;

    doc.text("ADDRESS", leftColX, leftYPos);
    doc.text(":", leftColX + headerLeftXOffset - 2, leftYPos);
    doc.setFontSize(headerValueFontSize);
    const addressLines = doc.splitTextToSize(customerAddress || "-", 38);
    doc.text(addressLines, leftColX + headerLeftXOffset, leftYPos);
    doc.setFontSize(5.6);
    leftYPos += Math.max(addressLines.length, 1) * headerValueLineHeight;

    doc.text("PHONE NUMBER", leftColX, leftYPos);
    doc.text(":", leftColX + headerLeftXOffset - 2, leftYPos);
    doc.setFontSize(headerValueFontSize);
    doc.text(customerPhone || "-", leftColX + headerLeftXOffset, leftYPos);
    doc.setFontSize(5.6);
    leftYPos += headerRowHeight;

    let rightYPos = startYPos;
    doc.text("NO", rightColX, rightYPos);
    doc.text(":", rightColX + headerRightXOffset - 2, rightYPos);
    doc.setFontSize(headerValueFontSize);
    doc.text(referenceNumber || "-", rightColX + headerRightXOffset, rightYPos);
    doc.setFontSize(5.6);
    rightYPos += headerRowHeight;

    doc.text("DATE", rightColX, rightYPos);
    doc.text(":", rightColX + headerRightXOffset - 2, rightYPos);
    doc.setFontSize(headerValueFontSize);
    doc.text(formattedDocumentDate, rightColX + headerRightXOffset, rightYPos);
    doc.setFontSize(5.6);
    rightYPos += headerRowHeight;

    doc.text("CLIENT ADVISOR", rightColX, rightYPos);
    doc.text(":", rightColX + headerRightXOffset - 2, rightYPos);
    doc.setFontSize(headerValueFontSize);
    doc.text(clientAdvisor, rightColX + headerRightXOffset, rightYPos);
    doc.setFontSize(5.6);
    rightYPos += headerRowHeight;

    doc.text("SHIPPED VIA", rightColX, rightYPos);
    doc.text(":", rightColX + headerRightXOffset - 2, rightYPos);
    doc.setFontSize(headerValueFontSize);
    doc.text(shippedVia, rightColX + headerRightXOffset, rightYPos);
    doc.setFontSize(5.6);

    yPos = Math.max(leftYPos, rightYPos) + 2.5;
    doc.setFontSize(productHeaderFontSize);
    doc.setFont("helvetica", "bold");

    if (showProductColumns) {
      yPos += 0.9;
      doc.line(leftColX, yPos, pageWidth - margin, yPos);
      yPos += 3;
      doc.text("DESCRIPTION", descColX, yPos);
      doc.text("QTY", qtyColX, yPos, { align: "center" });
      doc.text("UNIT PRICE", unitColX, yPos, { align: "right" });
      doc.text("AMOUNT", amountColX, yPos, { align: "right" });
      yPos += 1.4;
    }

    doc.line(leftColX, yPos, pageWidth - margin, yPos);
    yPos += showProductColumns ? 2.1 : 3.1;

    doc.setFontSize(4.8);
    doc.setFont("helvetica", "normal");

    return yPos;
  };

  const checklistMaxItemsPerColumn = isCustomerReceipt ? 4 : 5;
  const checklistColumnsPerRow = 2;
  const checklistColumnGap = isCustomerReceipt ? 1.4 : 2.4;
  const checklistMarkerOffset = 2.7;
  const checklistMarkerTopAlignOffset = 2;
  const checklistLineHeight = 3.2;

  // Typography mengikuti proporsi mockup A5.
  const productTitleFontSize = 6;
  const productTitleLineHeight = 3.4;
  const itemBodyFontSize = 6.5;
  const qtyFontSize = 6;
  const priceFontSize = 6;
  const productHeaderFontSize = 6;

  // Grid pada desain: kiri atas = kelengkapan, kanan atas = description/checklist,
  // kiri bawah = stamp, kanan bawah = card.
  const gridPhotoWidth = 19.5;
  const gridPhotoHeight = 23;
  // Stamp/Care Card are supporting detail photos. Keep their second row a
  // little shorter so a single-product Invoice stays on one A5 page without
  // changing the main Photo Kelengkapan/checklist alignment above it.
  const careCardDetailPhotoHeight = 18.5;
  const gridColumnGap = 0.6;
  const gridRowGap = 0.6;
  const stackedPhotoGap = 1.2;
  const checklistBottomGap = 1.6;
  const itemSeparatorTopGap = 1.4;
  const itemSeparatorBottomGap = 2.1;
  const rightContentX = descColX + gridPhotoWidth + gridColumnGap;
  const rightContentWidth = qtyColX - rightContentX - 4;

  const getVisibleImageEntries = (item) => {
    if (Array.isArray(item?.imageEntries) && item.imageEntries.length > 0) {
      return item.imageEntries.filter((entry) => entry?.url);
    }

    return [
      item?.mainImageUrl ? { label: "KELENGKAPAN", url: item.mainImageUrl } : null,
      item?.stampImageUrl ? { label: "STAMP", url: item.stampImageUrl } : null,
      item?.cardImageUrl ? { label: "CARD", url: item.cardImageUrl } : null,
    ].filter(Boolean);
  };

  const getStructuredPhotos = (item) => {
    const entries = getVisibleImageEntries(item);
    const mapped = entries.reduce((result, entry) => {
      result[entry.label] = entry.url;
      return result;
    }, {});

    return {
      comesWithUrl: mapped.KELENGKAPAN || item?.mainImageUrl || null,
      stampUrl: mapped.STAMP || item?.stampImageUrl || null,
      cardUrl: mapped.CARD || item?.cardImageUrl || null,
    };
  };

  const getChecklistValues = (item) =>
    String(item?.description || "")
      .split(", ")
      .map((value) => value.trim())
      .filter(Boolean);

  const getChecklistColumns = (item) => {
    const values = getChecklistValues(item);
    const columns = [];

    for (let index = 0; index < values.length; index += checklistMaxItemsPerColumn) {
      columns.push(values.slice(index, index + checklistMaxItemsPerColumn));
    }

    return columns;
  };

  const getChecklistRowGroups = (item) => {
    const columns = getChecklistColumns(item);
    if (columns.length === 0) return [];

    const groups = [];

    for (let index = 0; index < columns.length; index += checklistColumnsPerRow) {
      groups.push(columns.slice(index, index + checklistColumnsPerRow));
    }

    return groups;
  };

  const estimateChecklistColumnHeight = (values, textWidth) =>
    values.reduce((height, value) => {
      const lines = doc.splitTextToSize(value, textWidth);
      return height + Math.max(lines.length, 1) * checklistLineHeight;
    }, 0);

  const drawPhotoCell = (url, x, y, height = gridPhotoHeight) => {
    if (!url) return 0;

    const imageData = imageMap.get(url);

    if (imageData) {
      try {
        doc.addImage(imageData, "JPEG", x, y, gridPhotoWidth, height);
      } catch (error) {
        invoiceWarnings.push({
          type: "image-render",
          url,
          message: errorMessage(error),
        });
      }
    }

    return height;
  };

  const drawChecklistBlock = async (item, startX, startY, availableWidth) => {
    const rowGroups = getChecklistRowGroups(item);
    if (rowGroups.length === 0) return 0;

    doc.setFontSize(itemBodyFontSize);
    doc.setFont("helvetica", "normal");

    let yPos = startY + checklistMarkerTopAlignOffset;

    for (let groupIndex = 0; groupIndex < rowGroups.length; groupIndex += 1) {
      const group = rowGroups[groupIndex];
      const useSingleColumn = group.length === 1;
      const columnWidth = useSingleColumn
        ? availableWidth
        : (availableWidth - checklistColumnGap) / 2;
      const textWidth = columnWidth - checklistMarkerOffset;
      const columnStarts = useSingleColumn
        ? [startX]
        : [startX, startX + columnWidth + checklistColumnGap];
      const bottoms = [];

      for (let columnIndex = 0; columnIndex < group.length; columnIndex += 1) {
        const values = group[columnIndex];
        const columnX = columnStarts[columnIndex];
        let columnY = yPos;

        for (const value of values) {
          const lines = doc.splitTextToSize(value, textWidth);
          await drawDescriptionMarker(doc, svgElement, columnX, columnY - 2, invoiceWarnings);
          resetDrawingState();
          doc.setFontSize(itemBodyFontSize);
          doc.setFont("helvetica", "normal");
          doc.text(lines, columnX + checklistMarkerOffset, columnY);
          columnY += Math.max(lines.length, 1) * checklistLineHeight;
        }

        bottoms.push(columnY);
      }

      yPos = Math.max(...bottoms, yPos);

      if (groupIndex < rowGroups.length - 1) {
        yPos += 1.2;
      }
    }

    return Math.max(yPos - startY, 0);
  };

  const estimateChecklistBlockHeight = (item, availableWidth) => {
    const rowGroups = getChecklistRowGroups(item);
    if (rowGroups.length === 0) return 0;

    return checklistMarkerTopAlignOffset + rowGroups.reduce((height, group, groupIndex) => {
      const useSingleColumn = group.length === 1;
      const columnWidth = useSingleColumn
        ? availableWidth
        : (availableWidth - checklistColumnGap) / 2;
      const textWidth = columnWidth - checklistMarkerOffset;
      const groupHeight = Math.max(
        ...group.map((values) => estimateChecklistColumnHeight(values || [], textWidth)),
        0
      );
      const groupGap = groupIndex < rowGroups.length - 1 ? 1.2 : 0;

      return height + groupHeight + groupGap;
    }, 0);
  };

  const drawItemSeparator = (yPos) => {
    const separatorY = yPos + itemSeparatorTopGap;
    resetDrawingState();
    doc.line(leftColX, separatorY, pageWidth - margin, separatorY);
    return separatorY + itemSeparatorBottomGap;
  };

  const drawSkuSuffix = (item, x, titleY, titleLineCount) => {
    if (!item?.skuSuffix) return;

    const lastTitleLineY =
      titleY + Math.max(titleLineCount - 1, 0) * productTitleLineHeight;

    doc.setFontSize(productTitleFontSize);
    doc.setFont("helvetica", "normal");
    doc.text(`${item.skuSuffix}`, x, lastTitleLineY + 2.6);
  };

  const drawItem = async (item, yPos) => {
    const itemTopY = yPos;
    const { comesWithUrl, stampUrl, cardUrl } = getStructuredPhotos(item);
    const hasCardPhoto = Boolean(cardUrl);
    const hasLeftPhotos = Boolean(comesWithUrl || stampUrl);

    doc.setFont("helvetica", "bold");

    // Harga dan QTY sejajar dengan baris pertama nama product.
    const priceRowY = itemTopY + 2.7;
    doc.setFontSize(qtyFontSize);
    doc.text(String(getQty(item)), qtyColX, priceRowY, { align: "center" });
    doc.setFontSize(priceFontSize);
    doc.text(`IDR ${formatAmountForDisplay(getUnitAmount(item))}`, unitColX, priceRowY, { align: "right" });
    doc.text(`IDR ${formatAmountForDisplay(getLineAmount(item))}`, amountColX, priceRowY, { align: "right" });

    if (hasCardPhoto) {
      // Saat Stamp/Care Card tersedia, pertahankan urutan baca yang sama dengan
      // layout tanpa Card: nama + SKU di atas, lalu Photo Kelengkapan dan list
      // Kelengkapan mulai dari garis Y yang sama. Stamp/Card tetap di row bawah.
      const titleLines = doc.splitTextToSize(
        item.title,
        Math.max(qtyColX - descColX - 10, 28)
      );
      const titleY = itemTopY + 2.7;

      doc.setFontSize(productTitleFontSize);
      doc.setFont("helvetica", "bold");
      doc.text(titleLines, descColX, titleY);
      drawSkuSuffix(item, descColX, titleY, titleLines.length);

      const contentStartY =
        titleY + Math.max(titleLines.length - 1, 0) * productTitleLineHeight + 4.2;
      const checklistHeight = await drawChecklistBlock(
        item,
        rightContentX,
        contentStartY,
        rightContentWidth
      );
      const mainPhotoHeight = comesWithUrl
        ? drawPhotoCell(comesWithUrl, descColX, contentStartY)
        : 0;
      const topRowHeight = Math.max(mainPhotoHeight, checklistHeight, 0);

      const bottomY = contentStartY + topRowHeight + gridRowGap;
      const stampPhotoHeight = stampUrl
        ? drawPhotoCell(stampUrl, descColX, bottomY, careCardDetailPhotoHeight)
        : 0;
      const cardPhotoHeight = cardUrl
        ? drawPhotoCell(cardUrl, rightContentX, bottomY, careCardDetailPhotoHeight)
        : 0;
      const bottomRowHeight = Math.max(stampPhotoHeight, cardPhotoHeight, 0);

      const itemBottomY = bottomY + bottomRowHeight;
      return drawItemSeparator(itemBottomY);
    }

    // Tanpa Card, pertahankan layout lama supaya transaksi existing tidak
    // berubah drastis: title di atas, foto di kiri, checklist di kanan.
    const titleLines = doc.splitTextToSize(
      item.title,
      Math.max(qtyColX - descColX - 10, 28)
    );
    const titleY = itemTopY + 2.7;

    doc.setFontSize(productTitleFontSize);
    doc.setFont("helvetica", "bold");
    doc.text(titleLines, descColX, titleY);
    drawSkuSuffix(item, descColX, titleY, titleLines.length);

    const contentStartY =
      titleY + Math.max(titleLines.length - 1, 0) * productTitleLineHeight + 4.2;

    if (hasLeftPhotos) {
      const checklistHeight = await drawChecklistBlock(
        item,
        rightContentX,
        contentStartY,
        rightContentWidth
      );
      let leftHeight = 0;

      if (comesWithUrl) {
        leftHeight += drawPhotoCell(comesWithUrl, descColX, contentStartY);
      }

      if (stampUrl) {
        if (leftHeight > 0) leftHeight += stackedPhotoGap;
        leftHeight += drawPhotoCell(stampUrl, descColX, contentStartY + leftHeight);
      }

      const bodyBottomY =
        contentStartY + Math.max(leftHeight, checklistHeight, 0) + checklistBottomGap;
      return drawItemSeparator(bodyBottomY);
    }

    const checklistAvailableWidth = Math.max(
      qtyColX - descColX - (isCustomerReceipt ? 3 : 5),
      28
    );
    const checklistHeight = await drawChecklistBlock(
      item,
      descColX,
      contentStartY,
      checklistAvailableWidth
    );

    return drawItemSeparator(contentStartY + checklistHeight + checklistBottomGap);
  };

  const estimateItemHeight = (item) => {
    const { comesWithUrl, stampUrl, cardUrl } = getStructuredPhotos(item);
    const hasCardPhoto = Boolean(cardUrl);
    const hasLeftPhotos = Boolean(comesWithUrl || stampUrl);

    if (hasCardPhoto) {
      const legacyTitleWidth = Math.max(qtyColX - descColX - 10, 28);
      const titleLines = doc.splitTextToSize(item.title, legacyTitleWidth);
      const titleBlockHeight =
        2.7 +
        Math.max(titleLines.length - 1, 0) * productTitleLineHeight +
        4.2;
      const checklistHeight = estimateChecklistBlockHeight(item, rightContentWidth);
      const topRowHeight = Math.max(
        comesWithUrl ? gridPhotoHeight : 0,
        checklistHeight,
        0
      );
      const bottomRowHeight = Math.max(
        stampUrl ? careCardDetailPhotoHeight : 0,
        cardUrl ? careCardDetailPhotoHeight : 0,
        0
      );

      return (
        titleBlockHeight +
        topRowHeight +
        (bottomRowHeight > 0 ? gridRowGap + bottomRowHeight : 0)
      );
    }

    const legacyTitleWidth = Math.max(qtyColX - descColX - 10, 28);
    const titleLines = doc.splitTextToSize(item.title, legacyTitleWidth);
    const titleBlockHeight =
      2.7 +
      Math.max(titleLines.length - 1, 0) * productTitleLineHeight +
      4.2;

    if (hasLeftPhotos) {
      const checklistHeight = estimateChecklistBlockHeight(item, rightContentWidth);
      let leftHeight = 0;

      if (comesWithUrl) leftHeight += gridPhotoHeight;
      if (stampUrl) {
        if (leftHeight > 0) leftHeight += stackedPhotoGap;
        leftHeight += gridPhotoHeight;
      }

      return (
        titleBlockHeight +
        Math.max(leftHeight, checklistHeight, 0) +
        checklistBottomGap
      );
    }

    const checklistAvailableWidth = Math.max(
      qtyColX - descColX - (isCustomerReceipt ? 3 : 5),
      28
    );

    return (
      titleBlockHeight +
      estimateChecklistBlockHeight(item, checklistAvailableWidth) +
      checklistBottomGap
    );
  };

  const summaryLabelX = 105;
  const summaryColonX = 108;
  const summaryValueX = amountColX;
  const summaryRowHeight = 3.7;
  const summaryTableX = 67;
  const paymentTableX = isCustomerReceipt ? 82 : summaryTableX;
  const paymentTableRightX = pageWidth - margin;
  const paymentRowHeight = forceSinglePageCustomerReceipt ? 3.8 : 4.4;
  const paymentNoteX = paymentTableX;
  const paymentDateX = isCustomerReceipt ? 100 : 88;
  const paymentDateValueX = paymentDateX;
  const paymentAmountX = isCustomerReceipt ? summaryValueX : paymentTableRightX;
  const currencyValueFontSize = 6;
  const summaryCurrencyRowHeight = 4.4;
  const summaryEmphasisFontSize = 6;
  const summaryEmphasisRowHeight = 4.6;
  // Compact Invoice-only metrics are used when the totals block shares the
  // last product page. Keep typography readable while reserving a real
  // print-safe zone at the bottom of A5 paper.
  const invoiceCompactRowHeight = 3.05;
  const invoiceCompactCurrencyRowHeight = 4.2;
  const invoiceCompactEmphasisFontSize = 6;
  const invoiceCompactEmphasisRowHeight = 4.4;
  const invoicePrintSafeBottomMargin = 12;
  // INVOICE_COMPACT_TIGHT_FIT_V3
  // INVOICE_COMPACT_BALANCED_BLOCK_V4
  // INVOICE_COMPACT_BALANCED_BLOCK_V5
  const paidStampWidth = 27;
  const paidStampHeight = 16.5;
  const paidStampOpacity = 0.28;
  const paidStampTotalOverlapOffset = 4.5;
  const paidStampCareCardOverlapOffset = 6.0;
  const paidStampReceiptOutstandingOverlapOffset = 6.5;
  const paidStampLeftX =
    leftColX + (summaryTableX - leftColX - paidStampWidth) / 2;
  const paidStampSummaryOverlayX = Math.min(
    paymentTableRightX - paidStampWidth,
    Math.max(summaryTableX + 18, summaryValueX - paidStampWidth * 0.9)
  );

  const drawKeyValueRow = (label, value, yPos, options = {}) => {
    const {
      fontSize = currencyValueFontSize,
      valueFontSize = fontSize,
      fontStyle = "bold",
      rowHeight = summaryRowHeight,
    } = options;

    resetDrawingState();
    doc.setFontSize(fontSize);
    doc.setFont("helvetica", fontStyle);
    doc.text(label, summaryLabelX, yPos, { align: "right" });
    doc.text(":", summaryColonX, yPos);
    doc.setFontSize(valueFontSize);
    doc.text(value, summaryValueX, yPos, { align: "right" });
    return yPos + rowHeight;
  };

const drawFinancialSummary = (yPos, { compactInvoice = false } = {}) => {
  const regularRowOptions = compactInvoice
    ? {
        fontSize: currencyValueFontSize,
        valueFontSize: currencyValueFontSize,
        rowHeight: invoiceCompactCurrencyRowHeight,
      }
    : {
        valueFontSize: currencyValueFontSize,
        rowHeight: summaryCurrencyRowHeight,
      };
  const currencyRowOptions = compactInvoice
    ? {
        fontSize: currencyValueFontSize,
        valueFontSize: currencyValueFontSize,
        rowHeight: invoiceCompactCurrencyRowHeight,
      }
    : {
        valueFontSize: currencyValueFontSize,
        rowHeight: summaryCurrencyRowHeight,
      };
  const emphasisOptions = compactInvoice
    ? {
        fontSize: invoiceCompactEmphasisFontSize,
        valueFontSize: currencyValueFontSize,
        rowHeight: invoiceCompactEmphasisRowHeight,
      }
    : {
        fontSize: summaryEmphasisFontSize,
        valueFontSize: currencyValueFontSize,
        rowHeight: summaryEmphasisRowHeight,
      };

  yPos = drawKeyValueRow(
    "TOTAL QTY",
    `${totalQty} ${totalQty === 1 ? "ITEM" : "ITEMS"}`,
    yPos,
    regularRowOptions
  );
  yPos = drawKeyValueRow(
    "SUBTOTAL",
    `IDR ${formatAmountForDisplay(subtotal)}`,
    yPos,
    currencyRowOptions
  );

  if (voucherDiscountAmount > 0) {
    yPos = drawKeyValueRow(
      "VOUCHER",
      `— IDR ${formatAmountForDisplay(voucherDiscountAmount)}`,
      yPos,
      currencyRowOptions
    );
  }

  yPos = drawKeyValueRow(
    "SHIPMENT COST",
    `IDR ${formatAmountForDisplay(shipmentCostAmount)}`,
    yPos,
    currencyRowOptions
  );

  resetDrawingState();
  const totalTopLineOffset = compactInvoice ? 1.0 : 0.9;
  const totalTopGap = compactInvoice ? 1.0 : 1.9;
  const totalBottomLineOffset = compactInvoice ? 1.45 : 1.3;
  doc.line(summaryTableX, yPos - totalTopLineOffset, paymentTableRightX, yPos - totalTopLineOffset);
  yPos += totalTopGap;
  invoiceTotalRowY = yPos;
  yPos = drawKeyValueRow(
    "TOTAL",
    `IDR ${formatAmountForDisplay(netAmount)}`,
    yPos,
    emphasisOptions
  );
  resetDrawingState();
  if (!isCustomerReceipt) {
    doc.line(
      summaryTableX,
      yPos - totalBottomLineOffset,
      paymentTableRightX,
      yPos - totalBottomLineOffset
    );
  }

  return yPos + (compactInvoice ? 0 : 0.2);
};

const drawPaymentTableHeader =
 (yPos) => {
    resetDrawingState();
    if (isCustomerReceipt) {
      // Customer Receipt uses this as the single divider after TOTAL. Keep it
      // full-width, while the DP detail itself starts near the summary colon.
      doc.line(leftColX, yPos - 1.6, paymentTableRightX, yPos - 1.6);
    }
    doc.setFontSize(4.8);
    doc.setFont("helvetica", "bold");
    doc.text(
      "PAYMENT SUMMARY",
      isCustomerReceipt ? summaryColonX : paymentTableX,
      yPos,
      isCustomerReceipt ? { align: "right" } : undefined
    );
    yPos += 2.6;

    doc.line(paymentTableX, yPos, paymentTableRightX, yPos);
    yPos += 2.3;

    doc.setFontSize(4.5);
    doc.text("NOTES", paymentNoteX, yPos);
    doc.text("DATE", paymentDateX, yPos);
    doc.text("PAYMENT AMOUNT", paymentAmountX, yPos, { align: "right" });
    yPos += 1.3;
    doc.line(paymentTableX, yPos, paymentTableRightX, yPos);

    return yPos + 2.1;
  };

  const drawPaymentRow = (payment, paymentIndex, yPos) => {
    const confirmedAt =
      payment?.paid_at ||
      payment?.finance_confirmed_at ||
      payment?.updated_at ||
      payment?.created_at;

    resetDrawingState();
    doc.setFontSize(4.5);
    doc.setFont("helvetica", "normal");
    doc.text(`DP ${paymentIndex + 1}`, paymentNoteX, yPos);
    doc.text(
      formatPaymentSummaryDate(confirmedAt),
      paymentDateValueX,
      yPos
    );

    doc.setFontSize(currencyValueFontSize);
    doc.setFont("helvetica", "bold");
    doc.text(
      `— IDR ${formatAmountForDisplay(parseMoneyAmount(payment?.amount))}`,
      paymentAmountX,
      yPos,
      { align: "right" }
    );

    return yPos + paymentRowHeight;
  };

  const drawPaymentTotals = (yPos) => {
    resetDrawingState();
    doc.line(paymentTableX, yPos - 0.8, paymentTableRightX, yPos - 0.8);
    yPos += 2;
    receiptTotalPaidRowY = yPos;
    yPos = drawKeyValueRow(
      "TOTAL PAID",
      `IDR ${formatAmountForDisplay(totalPaid)}`,
      yPos,
      {
        fontSize: summaryEmphasisFontSize,
        valueFontSize: currencyValueFontSize,
        rowHeight: summaryEmphasisRowHeight,
      }
    );

    resetDrawingState();
    doc.line(paymentTableX, yPos - 0.8, paymentTableRightX, yPos - 0.8);
    yPos += 2;
    receiptOutstandingRowY = yPos;

    yPos = drawKeyValueRow(
      "OUTSTANDING BALANCE",
      `IDR ${formatAmountForDisplay(outstandingBalance)}`,
      yPos,
      {
        fontSize: summaryEmphasisFontSize,
        valueFontSize: currencyValueFontSize,
        rowHeight: summaryEmphasisRowHeight,
      }
    );
    return yPos;
  };

const drawInvoiceOutstanding = (yPos) => {
  // Invoice is only generated after settlement, so an IDR 0 outstanding row is
  // redundant. Customer Receipt keeps its Outstanding Balance row separately.
  return yPos;
};

const drawPaidStampAt =
 (yPos, stampXOverride = null) => {
    if (outstandingBalance > 0) return yPos;

    const stampX = stampXOverride ?? paidStampLeftX;
    const stampY = yPos;

    const drawFallback = () => {
      doc.setDrawColor(180, 0, 0);
      doc.setTextColor(180, 0, 0);
      doc.setLineWidth(0.6);
      doc.rect(stampX, stampY, 22, 9);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("PAID", stampX + 11, stampY + 6.5, { align: "center" });
      resetDrawingState();
    };

    if (paidStampImageData) {
      try {
        const supportsOpacity = typeof doc.setGState === "function" && typeof doc.GState === "function";

        if (supportsOpacity) {
          doc.saveGraphicsState();
          doc.setGState(new doc.GState({ opacity: paidStampOpacity }));
        }

        doc.addImage(
          paidStampImageData,
          "PNG",
          stampX,
          stampY,
          paidStampWidth,
          paidStampHeight
        );

        if (supportsOpacity) {
          doc.restoreGraphicsState();
        }
      } catch (error) {
        invoiceWarnings.push({
          type: "asset",
          url: PAID_STAMP_ASSET_URL,
          message: errorMessage(error),
        });
        drawFallback();
      }
    } else {
      drawFallback();
    }

    return stampY + paidStampHeight;
  };

  // Fully-paid Invoice product pages keep the existing PAID placement unless a
  // Care Card row is present. Care Card layouts move the single PAID stamp down
  // beside OUTSTANDING BALANCE. Customer Receipt is still stamped once at the end.
  const drawPaidStampOnProductPage = (itemBottomY, item) => {
    if (outstandingBalance > 0 || isCustomerReceipt) return;

    // PAID is intentionally rendered once inside the financial summary area so
    // the stamp overlays the final total amount instead of sitting in the
    // product block. This keeps Invoice and Customer Receipt consistent.
    return;
  };

  const customerReceiptTerms =
    "*Pembayaran telah kami terima. Tanda terima ini merupakan bukti bahwa pembayaran atas transaksi yang tercantum di atas telah diterima. Harap simpan tanda terima ini sebagai bukti pembayaran. Seluruh pembayaran yang telah dilakukan bersifat final dan tidak dapat dikembalikan (non-refundable). Tanda terima ini bukan merupakan faktur pajak.";
  const invoiceDisclaimer =
    "*Semua barang promo (harga khusus atau special price) yang sudah dibeli di BELLE&KATE Room adalah final. Kami tidak menerima klaim pengembalian dana atau pertukaran barang setelah barang dipesan. Terima kasih!";

  const drawTermsAtBottom = (minimumLineY = termsLineY) => {
    const actualTermsLineY = Math.max(termsLineY, minimumLineY);

    resetDrawingState();
    doc.line(leftColX, actualTermsLineY, pageWidth - margin, actualTermsLineY);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(isCustomerReceipt ? 4.4 : 4.2);

    const text = isCustomerReceipt ? customerReceiptTerms : invoiceDisclaimer;
    const lineHeight = isCustomerReceipt ? 2.2 : 2;
    const termLines = doc.splitTextToSize(text, pageWidth - margin * 2);
    doc.text(termLines, leftColX, actualTermsLineY + 4);
    return actualTermsLineY + 4 + termLines.length * lineHeight;
  };

  let yPos = drawPageHeader();
  let productCountOnPage = 0;

  const addProductPage = () => {
    drawFooter();
    doc.addPage();
    productCountOnPage = 0;
    return drawPageHeader();
  };

  const addSummaryPage = () => {
    drawFooter();
    doc.addPage();
    productCountOnPage = 0;
    return drawPageHeader({ showProductColumns: false });
  };

  for (const item of invoiceItemList) {
    const estimatedHeight = estimateItemHeight(item);
    const exceedsProductLimit = productCountOnPage >= itemsPerPage;
    const exceedsPageBody = yPos + estimatedHeight > productContentBottomY;

    if (productCountOnPage > 0 && (exceedsProductLimit || exceedsPageBody)) {
      yPos = addProductPage();
    }

    yPos = await drawItem(item, yPos);
    productCountOnPage += 1;

    if (outstandingBalance <= 0) {
      drawPaidStampOnProductPage(yPos, item);
    }
  }

  const hasCareCardPhoto = invoiceItemList.some((item) =>
    Boolean(getStructuredPhotos(item).cardUrl)
  );

  const voucherSummaryRowCount = voucherDiscountAmount > 0 ? 1 : 0;
  const financialSummaryHeight =
    summaryRowHeight +
    (2 + voucherSummaryRowCount) * summaryCurrencyRowHeight +
    1.9 +
    summaryEmphasisRowHeight +
    0.2;
  // CUSTOMER_RECEIPT_PHYSICAL_FOOTER_SAFE_ROLLBACK

  let useCompactInvoiceFinalLayout = false;
  let compactInvoiceRegionTopLineY = null;
  let compactInvoiceTermsLineY = null;
  let invoiceTotalRowY = null;
  let receiptTotalPaidRowY = null;
  let receiptOutstandingRowY = null;

if (isCustomerReceipt) {
  // Physical A5 paper already contains a printed footer. Keep the complete
  // Customer Receipt financial/payment summary inside the conservative PDF
  // body boundary so it can never be hidden behind that physical footer.
  const paymentHeaderHeight = 8.2;

  if (
    !forceSinglePageCustomerReceipt &&
    yPos + financialSummaryHeight + paymentHeaderHeight > contentBottomY
  ) {
    yPos = addSummaryPage();
  } else {
    yPos += forceSinglePageCustomerReceipt ? 0.8 : 1.4;
  }

  yPos = drawFinancialSummary(yPos);
  yPos += forceSinglePageCustomerReceipt ? 0.8 : 1.4;
  yPos = drawPaymentTableHeader(yPos);

  let paymentRowsOnPage = 0;
  const finalTotalsHeight = summaryEmphasisRowHeight * 2 + 2.4;
  const finalStampGap =
    outstandingBalance <= 0 && !forceSinglePageCustomerReceipt ? 1.5 + paidStampHeight : 0;
  const finalBlockHeight = finalTotalsHeight + finalStampGap + 2;

  for (let paymentIndex = 0; paymentIndex < confirmedReceiptPayments.length; paymentIndex += 1) {
    const payment = confirmedReceiptPayments[paymentIndex];
    const isLastPaymentRow = paymentIndex === confirmedReceiptPayments.length - 1;
    const limitY = isLastPaymentRow
      ? termsLineY - 5 - finalBlockHeight
      : contentBottomY;
    const exceedsPaymentBody = yPos + paymentRowHeight > limitY;

    if (!forceSinglePageCustomerReceipt && paymentRowsOnPage > 0 && exceedsPaymentBody) {
      yPos = addSummaryPage();
      yPos = drawPaymentTableHeader(yPos);
      paymentRowsOnPage = 0;
    }

    yPos = drawPaymentRow(payment, paymentIndex, yPos);
    paymentRowsOnPage += 1;
  }

  if (!forceSinglePageCustomerReceipt && yPos + finalBlockHeight > termsLineY - 5) {
    yPos = addSummaryPage();
    yPos = drawPaymentTableHeader(yPos);
  }

  yPos += forceSinglePageCustomerReceipt ? 0.8 : 1.6;
  yPos = drawPaymentTotals(yPos);

  if (outstandingBalance <= 0) {
    const outstandingRowY =
      receiptOutstandingRowY ?? receiptTotalPaidRowY ?? (yPos - summaryEmphasisRowHeight);
    const safeTopY = termsLineY - paidStampHeight + 0.5;
    const preferredStampY =
      outstandingRowY - paidStampHeight / 2 + paidStampReceiptOutstandingOverlapOffset;
    drawPaidStampAt(Math.min(preferredStampY, safeTopY), paidStampSummaryOverlayX);
  }

} else {
  const invoiceSummaryTopGap = 1.4;
  const invoiceOutstandingGap = 0;
  const invoiceFinancialSummaryHeight =
    summaryRowHeight +
    (2 + voucherSummaryRowCount) * summaryCurrencyRowHeight +
    1.9 +
    summaryEmphasisRowHeight +
    0.2;
  const invoiceOutstandingHeight = 0;
  const invoiceStampHeight = outstandingBalance <= 0 ? 1.6 + paidStampHeight : 0;
  const invoiceTermLines = doc.splitTextToSize(
    invoiceDisclaimer,
    pageWidth - margin * 2
  );
  const invoiceTermsReserveHeight =
    2.8 + 4 + invoiceTermLines.length * 2 + 4;
  const invoiceSummaryBottomY = reserveHeaderFooterLayout
    ? termsLineY - 5
    : pageHeight - invoiceTermsReserveHeight;
  const invoiceFinalBlockHeight =
    invoiceSummaryTopGap +
    invoiceFinancialSummaryHeight +
    invoiceOutstandingGap +
    invoiceOutstandingHeight +
    invoiceStampHeight;

  // When the totals share the last product page, use a denser Invoice-only
  // rhythm. The product layout is untouched; only the summary block is
  // compressed enough to reserve a 12 mm print-safe zone at the bottom.
  const compactInvoiceSummaryTopGap = 0.3;
  const compactInvoiceOutstandingGap = 0;
  const compactInvoiceFinancialSummaryHeight =
    invoiceCompactRowHeight +
    (2 + voucherSummaryRowCount) * invoiceCompactCurrencyRowHeight +
    0.8 +
    invoiceCompactEmphasisRowHeight;
  const compactInvoiceOutstandingHeight = 0;
  const compactInvoiceSummaryBottomY =
    yPos +
    compactInvoiceSummaryTopGap +
    compactInvoiceFinancialSummaryHeight +
    compactInvoiceOutstandingGap +
    compactInvoiceOutstandingHeight;
  const compactInvoiceTermsGap = 0.6;
  const compactInvoiceTermsLineCandidateY = Math.max(
    termsLineY,
    compactInvoiceSummaryBottomY + compactInvoiceTermsGap
  );
  const compactInvoiceTermsBottomY =
    compactInvoiceTermsLineCandidateY +
    4 +
    invoiceTermLines.length * 2;
  const invoicePrintSafeBottomY = reserveHeaderFooterLayout
    ? footerY - 5
    : pageHeight - invoicePrintSafeBottomMargin;
  const compactInvoiceFitsCurrentPage =
    compactInvoiceTermsBottomY <= invoicePrintSafeBottomY;

  // Prefer keeping the complete Invoice totals block on the last product page.
  // PAID uses the unused left side, while the disclaimer still respects the
  // explicit A5 print-safe bottom boundary.
  if (yPos + invoiceFinalBlockHeight > invoiceSummaryBottomY) {
    if (compactInvoiceFitsCurrentPage) {
      useCompactInvoiceFinalLayout = true;
      yPos += compactInvoiceSummaryTopGap;
      compactInvoiceRegionTopLineY = yPos - itemSeparatorBottomGap;
    } else {
      yPos = addSummaryPage();
    }
  } else {
    yPos += invoiceSummaryTopGap;
  }

  yPos = drawFinancialSummary(yPos, {
    compactInvoice: useCompactInvoiceFinalLayout,
  });
  yPos += useCompactInvoiceFinalLayout
    ? compactInvoiceOutstandingGap
    : invoiceOutstandingGap;
  yPos = drawInvoiceOutstanding(yPos, {
    compactInvoice: useCompactInvoiceFinalLayout,
  });

  if (outstandingBalance <= 0 && productCountOnPage > 0 && invoiceTotalRowY != null) {
    const summaryTermsLineY = useCompactInvoiceFinalLayout
      ? compactInvoiceTermsLineCandidateY
      : termsLineY;
    const safeTopY = summaryTermsLineY - paidStampHeight - (hasCareCardPhoto ? -0.5 : 3);
    const stampOverlapOffset = hasCareCardPhoto
      ? paidStampCareCardOverlapOffset
      : paidStampTotalOverlapOffset;
    const preferredStampY =
      invoiceTotalRowY - paidStampHeight / 2 + stampOverlapOffset;
    const productSafeTopY = compactInvoiceRegionTopLineY != null
      ? compactInvoiceRegionTopLineY + 0.8
      : preferredStampY;
    const stampY = Math.min(Math.max(preferredStampY, productSafeTopY), safeTopY);
    drawPaidStampAt(stampY, paidStampSummaryOverlayX);
  }

  if (useCompactInvoiceFinalLayout) {
    compactInvoiceTermsLineY = compactInvoiceTermsLineCandidateY;
  }
}

// Product pages are already stamped above. Draw another stamp only when the
// financial/payment summary was moved to a dedicated summary page.
if (!isCustomerReceipt && outstandingBalance <= 0 && productCountOnPage === 0) {

    if (invoiceTotalRowY != null) {
      const summaryTermsLineY = useCompactInvoiceFinalLayout
        ? (compactInvoiceTermsLineY ?? termsLineY)
        : termsLineY;
      const safeTopY = summaryTermsLineY - paidStampHeight - (hasCareCardPhoto ? -0.5 : 3);
      const stampOverlapOffset = hasCareCardPhoto
        ? paidStampCareCardOverlapOffset
        : paidStampTotalOverlapOffset;
      const preferredStampY =
        invoiceTotalRowY - paidStampHeight / 2 + stampOverlapOffset;
      drawPaidStampAt(Math.min(preferredStampY, safeTopY), paidStampSummaryOverlayX);
    } else {
      yPos += 1.6;
      yPos = drawPaidStampAt(yPos, paidStampSummaryOverlayX);
    }
  }

  const termsMinimumLineY = isCustomerReceipt
    ? termsLineY
    : useCompactInvoiceFinalLayout
      ? (compactInvoiceTermsLineY ?? yPos + 0.45)
      : yPos + 2.8;
  drawTermsAtBottom(termsMinimumLineY);
  drawFooter();

  const pageCount = doc.getNumberOfPages();
  for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
    doc.setPage(pageNumber);
    resetDrawingState();
    doc.setFontSize(4.7);
    doc.setFont("helvetica", "bold");
    doc.text(
      `PAGE ${pageNumber}/${pageCount}`,
      pageWidth - margin,
      reserveHeaderFooterLayout ? 34 : 44,
      { align: "right" }
    );
  }

  doc.invoiceWarnings = invoiceWarnings;
  return doc;
}

export async function printCustomerReceipt(e, deal, options = {}) {
  e?.preventDefault?.();

  const { displayHeaderAndFooter = true } = options;

  if (deal?.customer_receipt_available !== true) {
    throw new Error("Customer Receipt belum tersedia. Minimal satu payment harus sudah dikonfirmasi Finance.");
  }

  const confirmedAt = getLatestCustomerReceiptDate(deal);
  const doc = await buildPaginatedPaymentDocument(deal, {
    documentTitle: "CUSTOMER RECEIPT",
    summaryMode: "customer_receipt",
    documentDate: confirmedAt,
    displayBranding: displayHeaderAndFooter,
  });

  const referenceNumber = (deal?.reference_number || "customer-receipt").toString().trim();
  doc.save(buildDocumentDownloadFilename("customer-receipt", referenceNumber));

  return {
    warnings: Array.isArray(doc.invoiceWarnings) ? doc.invoiceWarnings : [],
  };
}

export async function printInvoice(e, deal, options = {}) {
  e?.preventDefault?.();

  const { displayHeaderAndFooter = true } = options;

  if (deal?.invoice_printable !== true) {
    throw new Error("Invoice belum tersedia. Payment harus sudah lunas dan settlement setelah dikonfirmasi Finance.");
  }

  const settlementPayment = getPaymentsAsc(deal)
    .slice()
    .reverse()
    .find((payment) => payment?.state === "settlement");

  const doc = await buildPaginatedPaymentDocument(deal, {
    documentTitle: "INVOICE",
    summaryMode: "invoice",
    documentDate: deal?.legacy_inventory_invoice_data?.transaction_date || settlementPayment?.paid_at || settlementPayment?.finance_confirmed_at || null,
    displayBranding: displayHeaderAndFooter,
  });
  const referenceNumber = (deal?.reference_number || "invoice").toString().trim();
  doc.save(buildDocumentDownloadFilename("invoice", referenceNumber));

  return {
    warnings: Array.isArray(doc.invoiceWarnings) ? doc.invoiceWarnings : [],
  };
}

export async function emailInvoice(e, deal, recipientEmails = null) {
  e?.preventDefault?.();

  if (deal?.invoice_printable !== true) {
    throw new Error("Invoice belum tersedia. Payment harus sudah lunas dan settlement setelah dikonfirmasi Finance.");
  }

  const settlementPayment = getPaymentsAsc(deal)
    .slice()
    .reverse()
    .find((payment) => payment?.state === "settlement");

  const doc = await buildPaginatedPaymentDocument(deal, {
    documentTitle: "INVOICE",
    summaryMode: "invoice",
    documentDate: settlementPayment?.paid_at || settlementPayment?.finance_confirmed_at || null,
    displayBranding: true,
  });

  let pdfBlob;

  try {
    pdfBlob = doc.output("blob");
  } catch {
    pdfBlob = new Blob([doc.output("arraybuffer")], { type: "application/pdf" });
  }

  const defaultEmail = (deal?.user?.email || "").toString().trim();
  const isMultipleEmailRequest = Array.isArray(recipientEmails);

  const rawEmails =
    Array.isArray(recipientEmails) && recipientEmails.length > 0
      ? recipientEmails
      : [defaultEmail];

  const emails = rawEmails
    .map((email) => (email || "").toString().trim())
    .filter(Boolean)
    .filter(
      (email, index, list) =>
        list.findIndex(
          (item) => item.toLowerCase() === email.toLowerCase()
        ) === index
    );

  if (emails.length === 0) {
    throw new Error("Minimal satu email tujuan harus diisi.");
  }

  const invalidEmails = emails.filter(
    (email) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  );

  if (invalidEmails.length > 0) {
    throw new Error(
      `Format email tidak valid: ${invalidEmails.join(", ")}`
    );
  }

  const sentEmails = [];
  let lastResponse = null;

  for (const toEmail of emails) {
    const formData = new FormData();

    formData.append(
      "invoice",
      pdfBlob,
      `invoice-${deal.reference_number}.pdf`
    );

    formData.append("to", toEmail);

    if (isMultipleEmailRequest) {
      formData.append("multiple_email", "true");
    }

    try {
      const res = await SalesDealsApi.sendInvoiceEmail({
        dealId: deal.id,
        formData,
      });

      lastResponse = res;
      sentEmails.push(toEmail);
    } catch (error) {
      const backendMessage = error?.response?.data?.message;
      const message =
        backendMessage ||
        error?.message ||
        "Unknown error";

      throw new Error(
        sentEmails.length > 0
          ? `Invoice berhasil dikirim ke ${sentEmails.length} email, tetapi gagal dikirim ke ${toEmail}: ${message}`
          : `Gagal mengirim invoice ke ${toEmail}: ${message}`
      );
    }
  }

  return {
    ...(emails.length === 1 ? (lastResponse?.data || {}) : {}),
    message:
      emails.length > 1
        ? `Invoice berhasil dikirim ke ${sentEmails.length} email.`
        : (lastResponse?.data?.message || "Invoice sent"),
    to: sentEmails,
    invoiceWarnings: Array.isArray(doc.invoiceWarnings)
      ? doc.invoiceWarnings
      : [],
  };
}
